import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Download, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCurrency, formatAccounting, formatAccountingText } from './stockAuditUtils';

/* ------------------------------------------------------------------ *
 * BREAKDOWN BUILDERS
 *
 *  Every headline figure and every ratio on the Calculate & Review
 *  step is backed by a "breakdown" object produced here. The builder
 *  pulls from data already on the page (calculation snapshot, costing
 *  report, KPI payload) so opening a modal is instant — no extra API
 *  call is made.
 *
 *  Every builder returns:
 *    {
 *      title, formula, explanation,
 *      columns?: [{ key, label, align, fmt }],
 *      rows?: [ { ...rowData } ],
 *      totals?: { label, value, fmt },
 *      figures?: [ { label, value, fmt } ],   // for ratios
 *      example?: string,                      // worked substitution
 *    }
 * ------------------------------------------------------------------ */

const fmtCur = v => formatCurrency(v || 0);
const fmtAcc = v => formatAccounting(v || 0);
// iter390 — Plain-string accounting formatter for use inside template
// literals (JSX-returning `fmtAcc` becomes "[object Object]" when coerced).
const fmtAccText = v => formatAccountingText(v);
const fmtQty = v => (v == null ? '—' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: 4 }));
const fmtPct = v => (v == null ? '—' : `${Number(v).toFixed(2)}%`);

/** Sum movements of specific txn_types across all products, returning
 *  per-product aggregate rows { product_id, product_name, qty, value }. */
function aggregateMovements(costingReport, txnTypes, { absValue = false } = {}) {
  const rows = [];
  (costingReport?.products || []).forEach(p => {
    let qty = 0;
    let value = 0;
    let count = 0;
    (p.movements || []).forEach(m => {
      if (!txnTypes.includes(m.txn_type)) return;
      qty += (m.qty_in || 0) + (m.qty_out || 0);
      const v = m.value || 0;
      value += absValue ? Math.abs(v) : v;
      count += 1;
    });
    if (count === 0) return;
    rows.push({
      product_id: p.product_id,
      product_name: p.product_name,
      stock_unit: p.stock_unit,
      qty,
      value,
      count,
    });
  });
  rows.sort((a, b) => (b.value || 0) - (a.value || 0));
  return rows;
}

export function buildBreakdown(type, { calculation, costingReport, sessionKpis, isCellarSession, isLocationSession, hasCellar = true }) {
  const c = calculation || {};
  const k = sessionKpis || {};
  const kpis = k.kpis || {};
  const methodology = c.headline_methodology || 'legacy';
  const isRevised = methodology === 'revised';
  // Under revised methodology the COS that drives GP/GP% is the INDIRECT
  // (stock-movement) figure. Under legacy it's the PLU-derived sales_at_cost.
  const cosForRatios = isRevised && (c.cost_of_sales_indirect != null)
    ? c.cost_of_sales_indirect
    : (c.sales_at_cost || 0);
  // Sales revenue basis: under revised we use Actual Receipts (excl VAT);
  // under legacy we use PLU Sales at Selling (excl VAT).
  const salesRevenueExcl = isRevised
    ? (c.accounts_sales_excl_vat || 0)
    : (c.sales_at_selling_excl_vat || 0);

  switch (type) {
    // ─────────────────────────── FLOW LINES ───────────────────────────
    case 'opening': {
      const rows = (costingReport?.products || [])
        .filter(p => (p.opening_value || 0) !== 0 || (p.opening_qty || 0) !== 0)
        .map(p => ({
          product_name: p.product_name,
          stock_unit: p.stock_unit,
          qty: p.opening_qty,
          unit_cost: p.opening_qty > 0 ? p.opening_value / p.opening_qty : 0,
          value: p.opening_value,
        }))
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      return {
        title: 'Opening Stock — breakdown',
        formula: 'Opening Stock = Σ (product opening_qty × unit cost)',
        explanation:
          'The saved opening stock value for this session. Each product contributes its opening quantity valued at the WAC carried forward from the previous session (or the manually entered opening cost when this is the first session).',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'unit_cost', label: 'Unit cost', align: 'right', fmt: fmtCur },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Opening Stock', value: c.opening_stock_value, fmt: fmtCur },
      };
    }

    case 'deliveries': {
      const rows = aggregateMovements(costingReport, ['Delivery']);
      const stale = c.self_consistency?.stale;
      return {
        title: 'Deliveries — breakdown',
        formula: 'Deliveries = Σ (delivery line stock_qty × unit cost)  — posted deliveries within the session period',
        explanation: stale
          ? `${c.self_consistency.message} The rows below are a LIVE read and may therefore not add up to the Total Deliveries figure shown (which is the saved, now-outdated snapshot value) — recalculate to refresh both.`
          : 'Only posted (non-draft) deliveries dated within this session\'s period contribute to the calculation. Case→bottle conversions are applied on the delivery line before valuation.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty in', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Lines', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Deliveries', value: c.deliveries_value, fmt: fmtCur },
      };
    }

    case 'credits': {
      const rows = aggregateMovements(costingReport, ['Credit'], { absValue: true });
      return {
        title: 'Credits — breakdown',
        formula: 'Credits = Σ (credit line stock_qty × running WAC)  — posted credits within the session period',
        explanation:
          'Returns to supplier reduce stock value but do NOT change the running WAC on the remaining stock. Only posted credits in the session period are included.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty out', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Lines', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Credits', value: c.credits_value, fmt: fmtCur },
      };
    }

    case 'cos': {
      const rows = aggregateMovements(costingReport, ['Sales'], { absValue: true });
      return {
        title: 'Cost of Sales (PLU — diagnostic) — breakdown',
        formula:
          'Cost of Sales (PLU) = Σ (sales_qty_in_stock_units × running WAC at time of sale)',
        explanation:
          'PLU / Sales-line-driven cost. Sales quantities from the Sales wizard (and posted Sale records in the session period) are converted to each product\'s stock unit (via product unit-map, measure_ml on the sale line, or MeasureDefinition fallback e.g. Pint=568ml), then valued at the running WAC. Under the Actual Receipts methodology this figure is DIAGNOSTIC ONLY — it does NOT drive GP, GP% or the headline result (those use the indirect COS).',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty sold', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Sales lines', align: 'right' },
          { key: 'value', label: 'COS (PLU)', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Cost of Sales (PLU)', value: c.sales_at_cost, fmt: fmtCur },
      };
    }

    case 'cos_indirect': {
      const op = c.opening_stock_value || 0;
      const de = c.deliveries_value || 0;
      const ti = c.transfers_in_value || 0;
      const cr = c.credits_value || 0;
      const to = c.transfers_out_value || 0;
      // 2026-04-21 rule 1 — COS uses MANAGEMENT only (Allowances excluded).
      // Fall back to legacy `adjustments_value` when a snapshot predates
      // the split buckets (older runs treated management+allowance as one).
      const mgmt = c.management_value != null ? c.management_value : (c.adjustments_value || 0);
      const ac = c.total_value || 0;
      const ci = c.cost_of_sales_indirect;
      const plu = c.sales_at_cost || 0;
      const vs = (ci != null) ? (plu - ci) : null;
      const example = (ci != null)
        ? `${fmtCur(op)} + ${fmtCur(de)}` +
          (ti ? ` + ${fmtCur(ti)}` : '') +
          ` − ${fmtCur(cr)}` +
          (to ? ` − ${fmtCur(to)}` : '') +
          ` ${mgmt >= 0 ? '+' : '−'} ${fmtCur(Math.abs(mgmt))}` +
          ` − ${fmtCur(ac)} = ${fmtCur(ci)}`
        : 'Actual Closing must be calculated before indirect COS can be derived.';
      return {
        title: 'Cost of Sales (indirect — stock-movement) — breakdown',
        formula:
          'COS (indirect) = Opening + Deliveries + Transfers In − Credits − Transfers Out ± Management − Actual Closing',
        explanation:
          'The cost of stock that actually left the shelves, derived ENTIRELY from stock movement — no dependency on PLU / Sales Mix. This is the figure that drives the headline methodology\'s Gross Profit and GP%. Management adjustments are included (negatives reduce COS, positives increase it). Allowances and Wastage are EXCLUDED — Allowances flow directly into the final deficit/surplus result instead. PLU-reported COS is shown alongside for reconciliation.',
        example,
        figures: [
          { label: 'Opening Stock', value: op, fmt: fmtCur },
          { label: '+ Deliveries', value: de, fmt: fmtCur },
          { label: '+ Transfers In', value: ti, fmt: fmtCur },
          { label: '− Credits', value: cr, fmt: fmtCur },
          { label: '− Transfers Out', value: to, fmt: fmtCur },
          { label: '± Management', value: mgmt, fmt: fmtAcc },
          { label: '− Actual Closing', value: ac, fmt: fmtCur },
          { label: '= COS (indirect)', value: ci, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: 'vs PLU COS', value: plu, fmt: fmtCur },
          { label: 'PLU − Indirect (diagnostic)', value: vs, fmt: v => v == null ? '—' : fmtAcc(v) },
        ],
      };
    }

    case 'sales_at_selling': {
      const vatInclusive = c.sales_at_selling || 0;
      const vatExclusive = c.sales_at_selling_excl_vat || 0;
      const implicitVat = Math.max(0, vatInclusive - vatExclusive);
      return {
        title: 'Recorded EPOS / PLU Sales (Inc VAT)',
        formula:
          'Recorded EPOS / PLU Sales (excl VAT) = Σ (recorded PLU qty × applicable selling price excl VAT)',
        explanation:
          'Based on recorded PLU quantities valued at the applicable selling price. Used in the Estimated vs Till reconciliation; it is not the Accounts Sales figure used as the audit denominator. The audit result uses Accounts Sales (till/PMS receipts) compared against Net Estimated Receipts (consumption-based).',
        figures: [
          { label: 'Recorded PLU Sales (excl VAT)', value: vatExclusive, fmt: fmtCur },
          { label: 'Recorded PLU Sales (incl VAT)', value: vatInclusive, fmt: fmtCur },
          { label: 'Implied VAT', value: implicitVat, fmt: fmtCur },
        ],
        example: `${fmtCur(vatInclusive)} (incl) − ${fmtCur(implicitVat)} VAT = ${fmtCur(vatExclusive)} (excl)`,
      };
    }

    case 'adjustments': {
      // Legacy catch-all — still used for snapshots pre-2026-04-21
      // that don't carry the management/allowance split.
      const types = ['Allowance', 'Management', 'Recovery', 'Unallocated', 'Transfer', 'Other'];
      const rows = aggregateMovements(costingReport, types);
      return {
        title: 'Adjustments (net, excl. Wastage) — breakdown',
        formula: 'Adjustments = Σ (Management) — Allowances are split into a separate bucket and applied to the final result.',
        explanation:
          'Historical catch-all view of non-wastage adjustments. From 2026-04-21 onwards Management (counts in COS) and Allowances (applied to final result) are tracked separately — open the specific buckets from the headline block.',
        columns: [
          { key: 'product_name', label: 'Product / bucket', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Entries', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtAcc },
        ],
        rows,
        totals: { label: 'Total Adjustments (net)', value: c.adjustments_value, fmt: fmtAcc },
      };
    }

    case 'management': {
      const rows = aggregateMovements(costingReport, ['Management']);
      const mgmt = c.management_value != null ? c.management_value : (c.adjustments_value || 0);
      const mgmtInclRaw = c.management_sales_incl_vat;
      const mgmtExclRaw = c.management_sales_excl_vat;
      const cosPctRaw = c.management_cos_budget_pct;
      const vatRaw = c.management_vat_rate;
      const hasRetail = mgmtInclRaw != null && Number(mgmtInclRaw) !== 0;
      const retailExample = hasRetail
        ? `Retail inc VAT ${fmtCur(mgmtInclRaw)} ÷ (1 + ${vatRaw != null ? Number(vatRaw).toFixed(0) : '20'}%/100) = Retail ex VAT ${fmtCur(mgmtExclRaw || 0)} · Retail ex VAT × ${cosPctRaw != null ? Number(cosPctRaw).toFixed(2) + '%' : '—'} COS Budget = Management Cost of Sales ${fmtAcc(mgmt)}`
        : 'No Management retail has been entered on the Adjustments step.';
      return {
        title: 'Management — Retail inc VAT → Management Cost of Sales',
        formula:
          'Retail ex VAT = Retail Sales inc VAT ÷ (1 + VAT%/100)   ·   Management Cost of Sales = Retail ex VAT × COS Budget %',
        explanation:
          'Management entries are captured as RETAIL sales inc VAT. The engine strips VAT to derive the Retail ex VAT, and applies the site\'s COS Budget % to derive the Management Cost of Sales. The Management Cost of Sales flows into the total Cost of Sales and Expected Closing (same role as the legacy Management value). The Retail ex VAT is subtracted from the raw surplus/shortage to arrive at the Final Result — the retail revenue diverted to Management usage reduces the apparent surplus by its ex-VAT value. Allowances and Wastage do NOT flow into this bucket.',
        example: retailExample,
        figures: [
          { label: 'Retail Sales inc VAT (entered)', value: mgmtInclRaw, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: '÷ (1 + VAT%/100)', value: vatRaw, fmt: v => v == null ? '—' : `${Number(v).toFixed(2)}%` },
          { label: '= Retail ex VAT', value: mgmtExclRaw, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: '× Site COS Budget %', value: cosPctRaw, fmt: v => v == null ? '—' : `${Number(v).toFixed(2)}%` },
          { label: '= Management Cost of Sales', value: mgmt, fmt: fmtAcc },
        ],
        columns: [
          { key: 'product_name', label: 'Product / bucket', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Entries', align: 'right' },
          { key: 'value', label: 'Value (raw inc VAT)', align: 'right', fmt: fmtAcc },
        ],
        rows,
        totals: { label: 'Total Management Cost of Sales', value: mgmt, fmt: fmtAcc },
      };
    }

    case 'allowance': {
      const rows = aggregateMovements(costingReport, ['Allowance']);
      const allow = c.allowance_value != null ? c.allowance_value : 0;
      const rawResult = (c.headline_methodology === 'revised')
        ? c.stocktake_result_revised
        : (c.stocktake_result_legacy ?? c.stocktake_result);
      const finalResult = (c.headline_methodology === 'revised')
        ? (c.stocktake_result_revised_adjusted ?? c.headline_stocktake_result)
        : (c.stocktake_result_legacy_adjusted ?? c.stocktake_result);
      const example = (rawResult != null)
        ? `${fmtAcc(rawResult)} + ${fmtAcc(allow)} = ${fmtAcc(finalResult)}`
        : '';
      return {
        title: 'Allowances — breakdown (direct deficit/surplus adjustment)',
        formula:
          'Allowances are EXCLUDED from Cost of Sales and Gross Profit. They are ADDED to the raw stocktake result (iter340 v3): Final Result = Raw Result + Allowances.',
        explanation:
          'Allowances (e.g. free staff drinks, sampling, manager comps) are not a cost of doing business — they are added back to the final deficit/surplus so the figure reflects what was actually consumed including the comped servings. A positive allowance increases the surplus (or reduces a deficit); a negative allowance does the opposite.',
        example,
        columns: [
          { key: 'product_name', label: 'Product / bucket', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Entries', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtAcc },
        ],
        rows,
        totals: { label: 'Total Allowances', value: allow, fmt: fmtAcc },
      };
    }

    case 'wastage': {
      const rows = aggregateMovements(costingReport, ['Wastage'], { absValue: true });
      return {
        title: 'Wastage — breakdown',
        formula: 'Wastage (KPI only) = Σ wastage qty × running WAC',
        explanation:
          'Wastage is recorded for reporting purposes but does NOT reduce Expected Closing under the current methodology (Phase 1 rule: wastage is an operational KPI, not a financial adjustment).',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Entries', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Wastage', value: c.wastage_value, fmt: fmtCur },
      };
    }

    case 'transfers_in': {
      const rows = aggregateMovements(costingReport, ['Received from Location']);
      return {
        title: 'Stock Received — breakdown',
        formula: 'Stock Received = Σ internal transfers INTO this location (at WAC)',
        explanation:
          isCellarSession
            ? 'Stock returned to the cellar from outlet locations during the audit period.'
            : hasCellar
              ? 'Stock received from the cellar or other locations (internal transfer) during the audit period.'
              : 'Stock received from other locations (internal transfer) during the audit period.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty in', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Transfers', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Received', value: c.transfers_in_value, fmt: fmtCur },
      };
    }

    case 'transfers_out': {
      const rows = aggregateMovements(costingReport, ['Issued to Location'], { absValue: true });
      return {
        title: 'Stock Issued — breakdown',
        formula: 'Stock Issued = Σ internal transfers OUT of this location (at WAC)',
        explanation:
          isCellarSession
            ? 'Stock issued from the cellar to outlet locations during the audit period.'
            : hasCellar
              ? 'Stock returned to the cellar or sent to other locations from this location during the audit period.'
              : 'Stock sent from this location to other locations during the audit period.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'qty', label: 'Qty out', align: 'right', fmt: fmtQty },
          { key: 'count', label: 'Transfers', align: 'right' },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Issued', value: c.transfers_out_value, fmt: fmtCur },
      };
    }

    case 'expected': {
      const rows = (costingReport?.products || [])
        .filter(p => (p.closing_value || 0) !== 0 || (p.closing_qty || 0) !== 0)
        .map(p => ({
          product_name: p.product_name,
          stock_unit: p.stock_unit,
          qty: p.closing_qty,
          unit_cost: p.closing_wac,
          value: p.closing_value,
        }))
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      const sales = (isCellarSession || isLocationSession)
        ? (c.transfers_out_value || 0)
        : (c.sales_at_cost || c.sales_value || 0);
      // Bug-3 fix: use management_value (the precise split field) with a
      // labelled "Mgmt" tag; fall back to adjustments_value for old snapshots.
      const mgmtForExample = c.management_value != null
        ? c.management_value
        : (c.adjustments_value || 0);
      const mgmtNum = Number(mgmtForExample);
      const mgmtSign = mgmtNum >= 0 ? '+' : '−';
      const example =
        `${fmtCur(c.opening_stock_value)} + ${fmtCur(c.deliveries_value)}` +
        (c.transfers_in_value ? ` + ${fmtCur(c.transfers_in_value)}` : '') +
        ` − ${fmtCur(c.credits_value)}` +
        ` − ${fmtCur(sales)}` +
        ` ${mgmtSign} ${fmtCur(Math.abs(mgmtNum))} (Mgmt)` +
        ` = ${fmtCur(c.expected_closing_value)}`;
      return {
        title: 'Expected Closing Stock — breakdown',
        formula: 'Expected Closing = Opening + Deliveries + Transfers In − Credits − Cost of Sales (or Transfers Out) ± Management',
        explanation:
          'The book value of stock that should be on site at the end of the period, derived from posted movements. Per-product expected closing = running qty after all movements, valued at running WAC. Only Management adjustments flow through this formula — Allowances are applied directly to the final deficit/surplus result (not here) and Wastage is KPI-only.',
        example,
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'qty', label: 'Qty', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'unit_cost', label: 'Running WAC', align: 'right', fmt: fmtCur },
          { key: 'value', label: 'Expected value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Expected Closing', value: c.expected_closing_value, fmt: fmtCur },
      };
    }

    case 'actual': {
      const rows = (c.lines || [])
        .filter(ln => (ln.line_value || 0) !== 0 || (ln.base_quantity || 0) !== 0)
        .map(ln => ({
          product_name: ln.product_name,
          stock_unit: ln.base_unit,
          qty: ln.base_quantity,
          unit_cost: ln.cost_per_base_unit,
          value: ln.line_value,
        }))
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      return {
        title: 'Actual Closing Stock — breakdown',
        formula: 'Actual Closing = Σ (counted qty in stock units × running WAC)',
        explanation:
          'The auditor\'s physical count, converted into each product\'s stock unit (using product unit-map, measure_ml or MeasureDefinition fallback) and valued at the running WAC. Manual cost overrides on exception lines win over the running WAC.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'qty', label: 'Counted qty', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'unit_cost', label: 'Unit cost', align: 'right', fmt: fmtCur },
          { key: 'value', label: 'Value', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Total Actual Closing', value: c.total_value, fmt: fmtCur },
      };
    }

    case 'result': {
      // iter767 — Net ESR = Gross − Allowances. Management PLU is a result-stage
      // DEDUCTION (negative adjustment). The stored `management_sales_incl_vat` is
      // POSITIVE (a cost); subtract it from the Trading Shortfall to get the result.
      const isRevisedR = (c.headline_methodology || 'legacy') === 'revised';
      const allow = c.allowance_value || 0;
      const mgmtSalesInclR = c.management_sales_incl_vat || 0;   // signed: + surplus, − add-back
      const mgmtAbs = Math.abs(mgmtSalesInclR);
      const wastageIncluded = c.wastage_included_value != null
        ? Number(c.wastage_included_value)
        : (c.wastage_value != null ? Number(c.wastage_value) : 0);
      const finalResult = isRevisedR
        ? (c.stocktake_result_revised_adjusted ?? c.headline_stocktake_result)
        : (c.stocktake_result_legacy_adjusted ?? c.stocktake_result);

      if (isRevisedR) {
        const accSales = c.accounts_sales_incl_vat ?? c.accounts_sales_excl_vat;
        const netEsr = c.estimated_receipts_incl_vat ?? c.estimated_receipts_excl_vat;
        const grossEsr = c.estimated_receipts_incl_vat_gross;

        // Detect if management is baked into the stored Net ESR (iter600 snapshots)
        // or is a result-stage add-back (old formula + new formula after revert).
        const derivedNetOldFormula = grossEsr != null ? +(grossEsr - allow).toFixed(2) : null;
        const mgmtBakedIn = netEsr != null && derivedNetOldFormula != null
          && Math.abs(netEsr - derivedNetOldFormula) > 0.05;
        const hasMgmtAddBack = !mgmtBakedIn && mgmtAbs > 0.005;

        const tradingShortfall = (accSales != null && netEsr != null) ? accSales - netEsr : null;
        const hasWastage = Math.abs(wastageIncluded) > 0.005;

        let exampleR = '';
        if (netEsr != null && accSales != null) {
          const eqParts = mgmtBakedIn
            ? `Gross ${fmtCur(grossEsr)} − Management ${fmtCur(mgmtAbs)} − Allowances ${fmtCur(allow)}`
            : `Gross ${fmtCur(grossEsr)} − Allowances ${fmtCur(allow)}`;
          exampleR = `Net Estimated Receipts: ${fmtCur(netEsr)}  (= ${eqParts})`;
          exampleR += `  ·  Actual Receipts ${fmtCur(accSales)} − Net ESR ${fmtCur(netEsr)} = Trading Shortfall ${fmtAccText(tradingShortfall)}`;
          if (hasMgmtAddBack) {
            exampleR += `  ·  ${fmtAccText(tradingShortfall)} ${mgmtSalesInclR > 0 ? '−' : '+'} Management PLU ${fmtCur(mgmtAbs)} = ${fmtAccText(+(tradingShortfall - mgmtSalesInclR).toFixed(2))}`;
          }
          if (hasWastage) {
            exampleR += `  ·  + Wastage ${fmtAccText(wastageIncluded)} = ${fmtAccText(finalResult)}`;
          }
        }

        const figures = [];
        if (grossEsr != null) {
          figures.push({ label: 'Gross Theoretical ESR (incl VAT)', value: grossEsr, fmt: fmtCur });
          if (mgmtBakedIn) {
            figures.push({ label: '− Management PLU (incl VAT)', value: mgmtSalesInclR, fmt: v => fmtAcc(-Math.abs(v)) });
          }
          figures.push({ label: '− Allowances (incl VAT)', value: allow, fmt: v => fmtAcc(-Math.abs(v)) });
        }
        figures.push({ label: '= Net Estimated Receipts', value: netEsr, fmt: fmtCur });
        figures.push({ label: 'Actual Receipts (incl VAT)', value: accSales, fmt: fmtCur });
        figures.push({ label: '= Trading Shortfall', value: tradingShortfall, fmt: v => v == null ? '—' : fmtAcc(v) });
        if (hasMgmtAddBack) {
          figures.push({ label: mgmtSalesInclR > 0 ? '− Management PLU (surplus taken in Actual Receipts)' : '+ Management PLU (add-back)', value: -mgmtSalesInclR, fmt: fmtAcc });
        }
        if (hasWastage) {
          figures.push({ label: '+ Wastage (incl in result)', value: wastageIncluded, fmt: fmtAcc });
        }
        figures.push({ label: '= Headline Result', value: finalResult, fmt: v => v == null ? '—' : fmtAcc(v) });

        const formulaLine = hasMgmtAddBack
          ? 'Result = Actual − Net ESR − Management PLU' + (hasWastage ? ' + Wastage (incl in result).' : '.')
          : 'Result = Actual Receipts (incl VAT) − Net Estimated Receipts' + (hasWastage ? ' + Wastage (incl in result).' : '.');
        return {
          title: 'Result — breakdown',
          formula: formulaLine,
          explanation:
            "The headline surplus or shortage. Net Estimated Receipts = Gross theoretical ESR minus Allowances (line cleaning, discounts, comped servings). Management PLU is then applied at the result stage with its sign: a positive entry (e.g. Cocktail Surplus, Wine by the Glass) is extra money already inside Actual Receipts, so it is subtracted; a negative entry is added back. Result = Actual minus Net ESR minus Management PLU (signed). If wastage entries are flagged 'Include in Result', they are also added. Positive = surplus; negative = shortage.",
          example: exampleR,
          figures,
        };
      }

      const rawResult = c.stocktake_result_legacy ?? c.stocktake_result;
      const rows = (c.lines || [])
        .filter(ln => (ln.variance_value || 0) !== 0)
        .map(ln => ({
          product_name: ln.product_name,
          stock_unit: ln.base_unit,
          expected_qty: ln.expected_qty,
          counted_qty: ln.base_quantity,
          variance_qty: ln.variance_qty,
          variance_value: ln.variance_value,
        }))
        .sort((a, b) => Math.abs(b.variance_value || 0) - Math.abs(a.variance_value || 0));
      const unallocated = (c.unallocated_variance || 0);
      const hasWastage = Math.abs(wastageIncluded) > 0.005;
      const example =
        `${fmtCur(c.total_value)} − ${fmtCur(c.expected_closing_value)} = ${fmtAccText(rawResult)}  ·  ${fmtAccText(rawResult)}${hasWastage ? ` + Wastage ${fmtAccText(wastageIncluded)}` : ''} = ${fmtAccText(finalResult)}`;
      return {
        title: 'Result (Legacy) — breakdown',
        formula: hasWastage
          ? 'Raw Legacy Result = Actual Closing − Expected Closing = Σ product variance + Unallocated.  Final Result = Raw Result + Wastage (incl in result).'
          : 'Result = Actual Closing − Expected Closing = Σ product variance + Unallocated variance.',
        explanation:
          "The legacy (valuation-based) surplus/shortage. Each product's variance plus the Unallocated line reconciles back to the raw result. If wastage entries are flagged 'Include in Result', they are added to the shortfall as additional losses.",
        example,
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'expected_qty', label: 'Expected', align: 'right', fmt: fmtQty },
          { key: 'counted_qty', label: 'Counted', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'variance_qty', label: 'Variance qty', align: 'right', fmt: fmtQty },
          { key: 'variance_value', label: 'Variance £', align: 'right', fmt: fmtAcc },
        ],
        rows: [
          ...rows,
          { product_name: 'Unallocated', stock_unit: '', expected_qty: null, counted_qty: null, variance_qty: null, variance_value: unallocated, _isFooter: true },
          ...(hasWastage ? [{ product_name: 'Wastage (incl in result)', stock_unit: '', expected_qty: null, counted_qty: null, variance_qty: null, variance_value: wastageIncluded, _isFooter: true }] : []),
        ],
        totals: { label: 'Final Result', value: finalResult, fmt: fmtAcc },
      };
    }

    case 'consumption_at_cost': {
      // iter386 — Drilldown for the wizard's "Consumption at Cost" KPI.
      // Mirrors the Estimated Receipts breakdown layout exactly, except each
      // row shows qty × WAC cost-per-base-unit instead of × selling price.
      // Backend source: snapshot `lines[]` (consumption_qty + cost_per_base_unit
      // per product, plus category / subcategory from estimated_receipts_breakdown).
      const linesRaw = c.lines || [];
      const erIndex = {};
      for (const er of (c.estimated_receipts_breakdown || [])) {
        if (er.beverage_product_id != null) erIndex[er.beverage_product_id] = er;
      }
      const rows = linesRaw
        .filter(ln => (ln.consumption_qty || 0) > 0)
        .map(ln => {
          const er = erIndex[ln.beverage_product_id] || {};
          const qty = Number(ln.consumption_qty || 0);
          const cost = Number(ln.cost_per_base_unit || 0);
          const value = +(qty * cost).toFixed(2);
          const groupLabel = (er.subcategory && String(er.subcategory).trim())
            ? String(er.subcategory).trim()
            : (er.category || '(uncategorised)');
          return {
            product_name: ln.product_name || er.product_name || '—',
            category: er.category || '(uncategorised)',
            subcategory: er.subcategory || '',
            group_label: groupLabel,
            stock_unit: ln.base_unit || er.stock_unit || '',
            qty,
            cost_per_unit: cost,
            value,
          };
        })
        .sort((a, b) => (b.value || 0) - (a.value || 0));
      return {
        title: 'Consumption at Cost — breakdown',
        formula:
          'Consumption at Cost = Σ (consumption_qty × cost per base unit / WAC)',
        explanation:
          "The cost of stock that actually moved off the shelves this period — per product. Consumption per product = opening + deliveries − credits − counted closing (at base unit). Cost per base unit is the running Weighted Average Cost at the time of closing. This is the denominator of the global Gross Profit formula (Actual Receipts − Consumption at Cost).",
        searchable: true,
        searchKeys: ['product_name', 'category', 'subcategory'],
        filterFields: [
          { key: 'category', label: 'Category' },
          { key: 'subcategory', label: 'Subcategory' },
        ],
        groupBy: 'group_label',
        exportFilenameBase: 'consumption-at-cost-breakdown',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'qty', label: 'Consumption qty', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
          { key: 'cost_per_unit', label: 'Cost/unit (WAC)', align: 'right', fmt: fmtCur },
          { key: 'value', label: 'Consumption £ (at cost)', align: 'right', fmt: fmtCur },
        ],
        rows,
        totals: { label: 'Consumption at Cost', value: c.cost_of_sales_indirect, fmt: fmtCur },
      };
    }

    case 'estimated_receipts': {
      // 2026-04-28 (iter329) — Switch to incl-VAT, group by product
      // category, and surface a search box. Backend now ships
      // category/subcategory/value_incl_vat/vat_rate per row.
      // 2026-05-07 — Stale-snapshot resilience: when a snapshot was
      // computed before iter329 (no value_incl_vat / category fields),
      // gracefully fall back to excl-VAT figures so the modal never
      // shows £0.00 prices, and surface a one-line banner explaining
      // the user should re-run Recalculate to refresh.
      const backendRows = c.estimated_receipts_breakdown || [];
      if (backendRows.length > 0) {
        const isLegacy = backendRows.some(
          r => r.value_incl_vat == null && r.value_excl_vat != null
        );
        const rows = backendRows.map(r => {
          // Per user's PRD example ("Draught Products, Bottled Beer,
          // Vodka, Gin, etc.") group by subcategory with category
          // fallback. Keeps the auditor's mental model intact.
          const groupLabel = (r.subcategory && String(r.subcategory).trim())
            ? String(r.subcategory).trim()
            : (r.category || '(uncategorised)');
          const valueIncl = r.value_incl_vat != null ? r.value_incl_vat : r.value_excl_vat;
          const priceIncl = r.selling_price_incl_vat != null ? r.selling_price_incl_vat : r.selling_price_excl_vat;
          return {
            product_name: r.product_name,
            category: r.category || '(uncategorised)',
            subcategory: r.subcategory || '',
            group_label: groupLabel,
            stock_unit: r.stock_unit,
            qty: r.consumption_qty,
            price_excl: r.selling_price_excl_vat,
            price_incl: priceIncl,
            vat_rate: r.vat_rate,
            value_excl: r.value_excl_vat,
            value: valueIncl,
            // 2026-05-07 (iter332) — three known sources mapped to short
            // human-readable labels for the breakdown UI.
            source: r.price_source === 'session_sales_weighted'
              ? 'session'
              : r.price_source === 'site_price_book'
                ? 'site book'
                : r.price_source === 'product_default_selling_price'
                  ? 'product default'
                  : (r.price_source || '—'),
          };
        });
        const grossValue = c.estimated_receipts_incl_vat_gross != null
          ? c.estimated_receipts_incl_vat_gross
          : null;
        const mgmtAmt = c.management_sales_incl_vat || 0;  // negative value
        const allowAmt = c.allowance_value || 0;             // positive value = deduction
        // Always use the stored Net ESR (correctly computed by the engine
        // version that saved this snapshot). Management is NOT baked into
        // Net ESR post-iter600-revert; it is a result-stage add-back.
        const netEsrValue = c.estimated_receipts_incl_vat != null
          ? c.estimated_receipts_incl_vat
          : grossValue != null
            ? +(grossValue - Math.abs(allowAmt)).toFixed(2)  // Gross − Allowances
            : c.estimated_receipts_excl_vat;
        const totalsLabel = (c.estimated_receipts_incl_vat != null && !isLegacy)
          ? 'Net Estimated Receipts (incl VAT)'
          : 'Estimated Receipts (excl VAT — re-run Recalculate to refresh as incl VAT)';
        // 2026-05-07 (iter332) — Surface products that have non-zero
        // consumption but no resolvable price (no session sale, no site
        // price book entry, no product default) so the auditor can fix
        // the price book before relying on Estimated Receipts.
        const skippedProducts = c.estimated_receipts_skipped_products || [];
        // Build footer rows: Gross (green) → Allowances deduction (red) → Net ESR.
        // iter767: Management PLU is a RESULT-STAGE deduction, NOT an ESR component.
        // Net ESR = Gross − Allowances only. Management appears in the result waterfall.
        const footerRows = [];
        const hasAnyAdj = Math.abs(allowAmt) > 0.005;
        // Net ESR: Gross − Allowances (management NOT deducted here).
        const adjustedNetEsr = grossValue != null
          ? +(grossValue - Math.abs(allowAmt)).toFixed(2)
          : netEsrValue;
        if (grossValue != null && hasAnyAdj) {
          footerRows.push({
            label: 'Gross Theoretical ESR (incl VAT)',
            value: grossValue,
            fmt: fmtCur,
            className: 'font-medium text-emerald-700',
          });
          if (Math.abs(allowAmt) > 0.005) {
            footerRows.push({
              label: 'Less: Allowances / Comps (incl VAT)',
              value: Math.abs(allowAmt),
              fmt: v => fmtAcc(-v),
              className: 'text-red-600',
            });
          }
        }
        return {
          title: 'Estimated Receipts — breakdown (incl VAT)',
          formula:
            'Net Estimated Receipts = Gross theoretical ESR − Allowances. Gross ESR = Σ (consumption_qty × weighted selling price incl VAT). Management PLU is a result-stage deduction (not shown here).',
          explanation:
            "The revenue the stock should have generated if sold at this session's weighted selling prices. Allowances (line-cleaning beer, discounts, complimentary servings) are deducted as legitimate revenue reductions. Management PLU (staff covers, comps, training pours) is handled separately as a result-stage deduction from the Stocktake Result — it does not reduce the Net Estimated Receipts figure shown here.",
          searchable: true,
          searchKeys: ['product_name', 'category', 'subcategory'],
          filterFields: [
            { key: 'category', label: 'Category' },
            { key: 'subcategory', label: 'Subcategory' },
          ],
          groupBy: 'group_label',
          exportFilenameBase: 'estimated-receipts-breakdown',
          legacyBanner: isLegacy
            ? 'This calculation snapshot was saved before the incl-VAT methodology rollout. Showing excl-VAT figures as fallback. Click "Recalculate" on the wizard to refresh with the new incl-VAT methodology and per-product categories.'
            : null,
          skippedProducts,
          columns: [
            { key: 'product_name', label: 'Product', align: 'left' },
            { key: 'qty', label: 'Consumption qty', align: 'right', fmt: fmtQty },
            { key: 'stock_unit', label: 'Unit', align: 'left' },
            { key: 'price_incl', label: 'Sell/unit (incl VAT)', align: 'right', fmt: fmtCur },
            { key: 'vat_rate', label: 'VAT %', align: 'right', fmt: v => v == null ? '—' : `${Number(v).toFixed(0)}%` },
            { key: 'source', label: 'Price src', align: 'left' },
            { key: 'value', label: 'Value (incl VAT)', align: 'right', fmt: fmtCur },
          ],
          rows,
          // footerRows renders a multi-row tfoot (Gross → Management → Allowances → Net)
          footerRows,
          totals: { label: totalsLabel, value: hasAnyAdj && grossValue != null ? adjustedNetEsr : netEsrValue, fmt: fmtCur },
        };
      }
      // Legacy fallback (consumption qty only — older snapshots)
      const rows = (c.lines || [])
        .filter(ln => (ln.consumption_qty || 0) > 0)
        .map(ln => ({
          product_name: ln.product_name,
          stock_unit: ln.base_unit,
          qty: ln.consumption_qty,
        }))
        .sort((a, b) => (b.qty || 0) - (a.qty || 0));
      return {
        title: 'Estimated Receipts — breakdown',
        formula:
          'Estimated Receipts (incl VAT) = Σ (consumption_qty_in_stock_units × weighted selling price incl VAT)',
        explanation:
          'This snapshot was calculated before per-product £ breakdown was available. Re-run the calculation to see £ values per product, including VAT.',
        columns: [
          { key: 'product_name', label: 'Product', align: 'left' },
          { key: 'qty', label: 'Consumption qty', align: 'right', fmt: fmtQty },
          { key: 'stock_unit', label: 'Unit', align: 'left' },
        ],
        rows,
        totals: { label: 'Estimated Receipts (incl VAT)', value: c.estimated_receipts_incl_vat ?? c.estimated_receipts_excl_vat, fmt: fmtCur },
      };
    }

    case 'accounts_sales': {
      const incl = c.accounts_sales_incl_vat;
      const excl = c.accounts_sales_excl_vat;
      const vat = incl != null && excl != null ? Math.max(0, incl - excl) : null;
      return {
        title: 'Actual Receipts — breakdown',
        formula: 'Actual Receipts (excl VAT) — entered in the Actual Receipts panel',
        explanation:
          'The actual till / accounting sales figure for the period (Actual Receipts). The excl-VAT figure is the benchmark the headline methodology compares against Estimated Receipts; the incl-VAT figure is shown for reference only — all downstream calculations (GP, result) use excl VAT.',
        figures: [
          { label: 'Actual Receipts (excl VAT)', value: excl, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: 'Actual Receipts (incl VAT)', value: incl, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: 'Implied VAT', value: vat, fmt: v => v == null ? '—' : fmtCur(v) },
        ],
      };
    }

    // ─────────────────────────── RATIOS ───────────────────────────
    case 'cos_pct': {
      const num = cosForRatios;
      const den = salesRevenueExcl;
      const val = den > 0 ? (num / den) * 100 : null;
      return {
        title: `Cost of Sales % — breakdown (${isRevised ? 'Indirect (stock-movement)' : 'Legacy / PLU'})`,
        formula: isRevised
          ? 'COS % = Cost of Sales (indirect) ÷ Actual Receipts (excl VAT) × 100'
          : 'COS % = Cost of Sales (PLU) ÷ Sales at Selling (excl VAT) × 100',
        explanation:
          'The share of revenue consumed by cost of goods. Under the Actual Receipts methodology this uses indirect (stock-movement) COS and Actual Receipts — PLU is diagnostic only. All figures excl VAT.',
        figures: [
          { label: isRevised ? 'Cost of Sales (indirect)' : 'Cost of Sales (PLU)', value: num, fmt: fmtCur },
          { label: isRevised ? 'Actual Receipts (excl VAT)' : 'Sales at Selling (excl VAT)', value: den, fmt: fmtCur },
          { label: 'COS %', value: val, fmt: fmtPct },
        ],
        example: den > 0
          ? `${fmtCur(num)} ÷ ${fmtCur(den)} × 100 = ${fmtPct(val)}`
          : `Revenue basis is 0 — COS % cannot be computed.`,
      };
    }

    case 'gp_pct': {
      const cost = cosForRatios;
      const sales = salesRevenueExcl;
      const gp = sales - cost;
      const val = sales > 0 ? (gp / sales) * 100 : null;
      return {
        title: `Gross Profit % — breakdown (${isRevised ? 'Indirect (stock-movement)' : 'Legacy / PLU'})`,
        formula: isRevised
          ? 'GP % = (Actual Receipts excl VAT − COS indirect) ÷ Actual Receipts excl VAT × 100'
          : 'GP % = (Sales at Selling excl VAT − COS PLU) ÷ Sales at Selling excl VAT × 100',
        explanation:
          'Margin on stock sold, before overheads. Headline GP uses actual till receipts (Actual Receipts excl VAT) vs indirect COS; Legacy GP uses PLU-derived figures. All excl VAT.',
        figures: [
          { label: isRevised ? 'Actual Receipts (excl VAT)' : 'Sales at Selling (excl VAT)', value: sales, fmt: fmtCur },
          { label: isRevised ? 'Cost of Sales (indirect)' : 'Cost of Sales (PLU)', value: cost, fmt: fmtCur },
          { label: 'Gross Profit', value: gp, fmt: fmtCur },
          { label: 'GP %', value: val, fmt: fmtPct },
        ],
        example: sales > 0
          ? `(${fmtCur(sales)} − ${fmtCur(cost)}) ÷ ${fmtCur(sales)} × 100 = ${fmtCur(gp)} ÷ ${fmtCur(sales)} × 100 = ${fmtPct(val)}`
          : `Revenue basis is 0 — GP % cannot be computed.`,
      };
    }

    case 'gp': {
      const cost = cosForRatios;
      const sales = salesRevenueExcl;
      const gp = sales - cost;
      return {
        title: `Gross Profit — breakdown (${isRevised ? 'Indirect (stock-movement)' : 'Legacy / PLU'})`,
        formula: isRevised
          ? 'Gross Profit = Actual Receipts (excl VAT) − Cost of Sales (indirect)'
          : 'Gross Profit = Sales at Selling (excl VAT) − Cost of Sales (PLU)',
        explanation:
          'Headline GP uses Actual Receipts and indirect COS (no PLU dependency). Legacy GP uses PLU retail and PLU-driven COS. All figures excl VAT.',
        figures: [
          { label: isRevised ? 'Actual Receipts (excl VAT)' : 'Sales at Selling (excl VAT)', value: sales, fmt: fmtCur },
          { label: isRevised ? 'Cost of Sales (indirect)' : 'Cost of Sales (PLU)', value: cost, fmt: fmtCur },
          { label: 'Gross Profit', value: gp, fmt: fmtCur },
        ],
        example: `${fmtCur(sales)} − ${fmtCur(cost)} = ${fmtCur(gp)}`,
      };
    }

    case 'yield_pct': {
      // iter600 — Yield denominator updated after Net ESR change.
      // Yield % = Actual ÷ (Net ESR + Allowances) × 100
      //         = Actual ÷ (Gross ESR + Management)   [since Net ESR = Gross + Mgmt - Allow]
      // This gives "Gross ESR after management but before allowances" as denominator,
      // which matches the legacy formula (e.g. 94.8% for Mercure Birmingham West ST-0019).
      const netEsr = c.estimated_receipts_incl_vat != null
        ? c.estimated_receipts_incl_vat
        : (c.estimated_receipts_excl_vat != null ? c.estimated_receipts_excl_vat : c.sales_at_selling);
      const actualReceipts = c.accounts_sales_incl_vat != null
        ? c.accounts_sales_incl_vat
        : (c.accounts_sales_excl_vat != null ? c.accounts_sales_excl_vat : null);
      const allowAmt = c.allowance_value || 0;
      // Denominator = Net ESR + Allowances = Gross ESR + Management (equivalent)
      const denom = (netEsr != null) ? Number(netEsr) + Number(allowAmt) : null;
      const grossEsr = c.estimated_receipts_incl_vat_gross;
      const val = kpis.yield_pct;
      return {
        title: 'Yield % — breakdown',
        formula: 'Yield % = Actual Receipts (incl VAT) ÷ (Net Estimated Receipts + Allowances) × 100',
        explanation:
          "How the cash the till actually took compares to the theoretical yield (after Management PLU but before Allowances). The denominator adds Allowances back to Net ESR, giving the 'before-allowances' baseline (= Gross ESR + Management). This matches the legacy stocktake yield formula. 100% = perfect match; >100% = till banked MORE than theoretical (possible over-ring or under-measure); <100% = banked LESS (possible shrinkage, waste, or unbanked sales).",
        figures: [
          { label: 'Actual Receipts (incl VAT)', value: actualReceipts, fmt: v => v == null ? '— (not captured)' : fmtCur(v) },
          { label: 'Net Estimated Receipts (incl VAT)', value: netEsr, fmt: fmtCur },
          { label: '+ Allowances (incl VAT)', value: allowAmt, fmt: fmtCur },
          { label: '= Yield Denominator (incl VAT)', value: denom, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: 'Gross Theoretical ESR', value: grossEsr, fmt: v => v != null ? fmtCur(v) : '—' },
          { label: 'Yield %', value: val, fmt: fmtPct },
        ],
        example: (denom != null && denom > 0 && actualReceipts != null)
          ? `${fmtCur(actualReceipts)} ÷ ${fmtCur(denom)} × 100 = ${fmtPct(val)}`
          : 'Denominator is 0 or negative — Yield % cannot be computed.',
      };
    }

    case 'variance_pct': {
      const result = c.headline_stocktake_result ?? c.stocktake_result_revised_adjusted ?? c.stocktake_result_legacy_adjusted ?? c.stocktake_result ?? 0;
      const ar = c.accounts_sales_incl_vat ?? c.accounts_sales_excl_vat ?? 0;
      const val = (ar && ar > 0) ? (result / ar) * 100 : null;
      return {
        title: 'Variance % — breakdown',
        formula: 'Variance % = Result ÷ Actual Receipts (incl VAT) × 100',
        explanation:
          'How far the headline Result deviates from the till receipts. Target: within ±2%. Above ±5% usually warrants investigation. Uses the official methodology — the same figure the V&V check now reports.',
        figures: [
          { label: 'Result', value: result, fmt: fmtAcc },
          { label: 'Actual Receipts (incl VAT)', value: ar, fmt: fmtCur },
          { label: 'Variance %', value: val, fmt: v => v == null ? '—' : fmtPct(v) },
        ],
        example: (ar && ar > 0)
          ? `${fmtAcc(result)} ÷ ${fmtCur(ar)} × 100 = ${val == null ? '—' : fmtPct(val)}`
          : `Actual Receipts (incl VAT) is 0 — Variance % cannot be computed.`,
      };
    }

    case 'stockholding': {
      // 2026-04-21 rule 2 — authoritative formula from the backend:
      //     Stockholding Days = Actual Closing Stock ÷ Avg Daily COS
      //     Avg Daily COS = headline COS ÷ session period days
      // (period_days = session.period_to − session.period_from + 1)
      // Falls back to the legacy avg-stock / trading_days formula if the
      // snapshot predates the backend-emitted fields.
      const closing = c.total_value || 0;
      const cost = cosForRatios;
      const periodDaysBackend = c.period_days;
      const avgDailyCosBackend = c.avg_daily_cos;
      const stockholdingBackend = c.stockholding_days;

      const fallbackTradingDays = k.trading_days || null;
      const fallbackAvgStock = ((c.opening_stock_value || 0) + closing) / 2;
      const fallbackDailyCos = fallbackTradingDays && fallbackTradingDays > 0
        ? cost / fallbackTradingDays : null;
      const fallbackVal = (fallbackAvgStock > 0 && fallbackDailyCos && fallbackDailyCos > 0)
        ? Math.round(fallbackAvgStock / fallbackDailyCos) : null;

      const periodDays = periodDaysBackend != null ? periodDaysBackend : fallbackTradingDays;
      const dailyCos = avgDailyCosBackend != null ? avgDailyCosBackend : fallbackDailyCos;
      const val = stockholdingBackend != null ? stockholdingBackend : fallbackVal;
      const usingBackend = stockholdingBackend != null;
      return {
        title: 'Stockholding Days — breakdown',
        formula: 'Stockholding Days = Actual Closing Stock ÷ Average Daily Cost of Sales. Avg Daily COS = headline Cost of Sales ÷ session period days.',
        explanation:
          `How many days' worth of stock is currently on the shelf, based on the headline COS (${isRevised ? 'indirect / stock-movement' : 'PLU-led'}) over the session period. A lower number means stock is turning quickly; a higher number means stock is piling up. ${usingBackend ? 'Values come directly from the calculation snapshot.' : 'Legacy fallback — avg stock ÷ daily COS using trading days.'}`,
        figures: [
          { label: 'Actual Closing Stock', value: closing, fmt: fmtCur },
          { label: isRevised ? 'Cost of Sales (indirect)' : 'Cost of Sales (PLU)', value: cost, fmt: fmtCur },
          { label: 'Session period (days)', value: periodDays, fmt: v => v == null ? '—' : String(v) },
          { label: 'Avg Daily Cost of Sales', value: dailyCos, fmt: v => v == null ? '—' : fmtCur(v) },
          { label: 'Stockholding (days)', value: val, fmt: v => v == null ? '—' : `${Number(v).toFixed(1)} days` },
        ],
        example: (dailyCos && dailyCos > 0)
          ? `${fmtCur(closing)} ÷ ${fmtCur(dailyCos)} = ${val != null ? `${Number(val).toFixed(1)} days` : '—'}`
          : `Avg Daily Cost of Sales is 0 — Stockholding cannot be computed.`,
      };
    }

    default:
      return null;
  }
}

/* ------------------------------------------------------------------ *
 * DIALOG
 * ------------------------------------------------------------------ */

export function StockAuditBreakdownDialog({ open, onClose, breakdown }) {
  const data = breakdown;
  const [search, setSearch] = useState('');
  // iter425 — Tabbed views (e.g. "By Location" vs "By Product") for
  // the multi-location KPI drill-down. The dialog reads `breakdown.views`
  // and switches the column/row source based on the active tab.
  const views = data?.views || null;
  const [activeViewKey, setActiveViewKey] = useState(null);
  React.useEffect(() => {
    if (views && views.length > 0) setActiveViewKey(views[0].key);
    else setActiveViewKey(null);
  }, [data?.title, views]);

  const activeView = useMemo(() => {
    if (!views || !activeViewKey) return null;
    return views.find(v => v.key === activeViewKey) || views[0];
  }, [views, activeViewKey]);

  // Reset search when the dialog opens with a different breakdown OR
  // when the user switches between views.
  React.useEffect(() => { setSearch(''); }, [data?.title, activeViewKey]);

  // Keep columns/rows referentially stable for the render. When
  // `views` are present, the active view supplies the table; the
  // top-level title/formula/explanation/example still come from `data`.
  const { columns, rows, totals, figures, footerRows } = useMemo(() => {
    if (activeView) {
      return {
        columns: activeView.columns || null,
        rows: activeView.rows || null,
        totals: activeView.totals || null,
        figures: activeView.figures || null,
        footerRows: activeView.footerRows || null,
      };
    }
    return {
      columns: data?.columns || null,
      rows: data?.rows || null,
      totals: data?.totals || null,
      figures: data?.figures || null,
      footerRows: data?.footerRows || null,
    };
  }, [data, activeView]);

  // 2026-04-28 (iter329) — Searchable + groupable breakdown rendering.
  // 2026-05-07 (iter330) — Adds category + subcategory dropdown filters,
  // Excel export and a stale-snapshot banner.
  const isSearchable = !!(activeView?.searchable ?? data?.searchable);
  const groupBy = (activeView?.groupBy ?? data?.groupBy) || null;
  const searchKeys = (activeView?.searchKeys ?? data?.searchKeys) || (columns ? columns.map(c => c.key) : []);
  const filterFields = (activeView?.filterFields ?? data?.filterFields) || [];
  const legacyBanner = data?.legacyBanner || null;
  const skippedProducts = data?.skippedProducts || [];
  const exportFilenameBase = data?.exportFilenameBase || 'breakdown';

  // One state slot per declared filter field (e.g. category, subcategory).
  const [filterValues, setFilterValues] = useState({});
  React.useEffect(() => { setFilterValues({}); }, [data?.title]);

  // Distinct values for each filter dropdown — derived from the FULL
  // (un-filtered) row set so the user always sees every option.
  const filterOptions = useMemo(() => {
    const opts = {};
    if (!rows) return opts;
    for (const f of filterFields) {
      const set = new Set();
      for (const r of rows) {
        const v = r[f.key];
        if (v != null && String(v).trim() !== '') set.add(String(v));
      }
      opts[f.key] = Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    return opts;
  }, [rows, filterFields]);

  const filteredRows = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (isSearchable && search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(r => searchKeys.some(k => {
        const v = r[k];
        return v != null && String(v).toLowerCase().includes(q);
      }));
    }
    for (const f of filterFields) {
      const sel = filterValues[f.key];
      if (sel && sel !== '__all__') {
        out = out.filter(r => String(r[f.key] || '') === sel);
      }
    }
    return out;
  }, [rows, search, isSearchable, searchKeys, filterFields, filterValues]);

  const groupedRows = useMemo(() => {
    if (!groupBy || !filteredRows) return null;
    const map = new Map();
    for (const r of filteredRows) {
      const key = r[groupBy] || '(uncategorised)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    // Sort groups: alphabetical, but uncategorised last
    return Array.from(map.entries())
      .sort(([a], [b]) => {
        if (a === '(uncategorised)') return 1;
        if (b === '(uncategorised)') return -1;
        return String(a).localeCompare(String(b));
      });
  }, [filteredRows, groupBy]);

  if (!data) return null;

  // 2026-05-07 (iter330) — Excel export uses the CURRENTLY filtered rows
  // (search + dropdown filters honoured) and respects the group-by, so
  // the output mirrors what the user sees on screen including subtotals.
  const handleExportExcel = () => {
    if (!columns || !filteredRows) return;
    const headerRow = columns.map(c => c.label);
    const sheetData = [headerRow];
    const totalsCol = columns[columns.length - 1];

    if (groupedRows && groupedRows.length > 0) {
      groupedRows.forEach(([groupKey, groupRowsArr]) => {
        const subtotal = groupRowsArr.reduce(
          (acc, r) => acc + (Number(r[totalsCol.key]) || 0), 0
        );
        // Group header row
        sheetData.push([
          `${String(groupKey).replace(/_/g, ' ').toUpperCase()} (${groupRowsArr.length})`,
          ...new Array(columns.length - 2).fill(''),
          subtotal,
        ]);
        // Member rows — raw values (not the formatted strings) so Excel
        // can sum/sort them natively.
        groupRowsArr.forEach(r => {
          sheetData.push(columns.map(col => {
            const raw = r[col.key];
            // Currency / number columns: pass through as Number for Excel
            const isNumeric = col.align === 'right' && (col.key === 'value' || col.key === 'value_excl' || col.key === 'qty' || col.key === 'price_incl' || col.key === 'price_excl' || col.key === 'vat_rate');
            if (isNumeric) return raw == null ? null : Number(raw);
            return raw == null ? '' : String(raw);
          }));
        });
      });
    } else {
      filteredRows.forEach(r => {
        sheetData.push(columns.map(col => {
          const raw = r[col.key];
          const isNumeric = col.align === 'right' && (col.key === 'value' || col.key === 'value_excl' || col.key === 'qty' || col.key === 'price_incl' || col.key === 'price_excl' || col.key === 'vat_rate');
          if (isNumeric) return raw == null ? null : Number(raw);
          return raw == null ? '' : String(raw);
        }));
      });
    }

    if (totals) {
      const totalsRow = new Array(columns.length).fill('');
      totalsRow[columns.length - 2] = totals.label;
      totalsRow[columns.length - 1] = Number(totals.value) || 0;
      sheetData.push(totalsRow);
    }

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Breakdown');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `${exportFilenameBase}-${stamp}.xlsx`);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-breakdown-dialog">
        <DialogHeader>
          <DialogTitle>{data.title}</DialogTitle>
          <DialogDescription className="text-xs text-slate-600 pt-1 font-mono break-words">
            {data.formula}
          </DialogDescription>
        </DialogHeader>

        {data.explanation && (
          <p className="text-xs text-slate-600 leading-relaxed" data-testid="breakdown-explanation">
            {data.explanation}
          </p>
        )}

        {data.example && (
          <div
            className="text-xs bg-slate-50 border rounded px-3 py-2 font-mono leading-relaxed break-words"
            data-testid="breakdown-example"
          >
            {data.example}
          </div>
        )}

        {views && views.length > 1 && (
          <div className="flex gap-1 border-b" data-testid="breakdown-view-tabs">
            {views.map(v => (
              <button
                key={v.key}
                type="button"
                onClick={() => setActiveViewKey(v.key)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  activeViewKey === v.key
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`breakdown-view-tab-${v.key}`}
              >
                {v.label}
                {v.rows && <span className="ml-1.5 text-[10px] text-slate-400">({v.rows.length})</span>}
              </button>
            ))}
          </div>
        )}

        {figures && figures.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="breakdown-figures">
            {figures.map(f => (
              <div key={f.label} className="rounded border bg-white px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</p>
                <p className="text-sm font-semibold tabular-nums">{f.fmt ? f.fmt(f.value) : f.value}</p>
              </div>
            ))}
          </div>
        )}

        {legacyBanner && (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-2 text-amber-900 text-xs"
            data-testid="breakdown-legacy-banner"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>{legacyBanner}</p>
          </div>
        )}

        {skippedProducts.length > 0 && (
          <div
            className="rounded-md border border-rose-300 bg-rose-50 p-3 flex gap-2 text-rose-900 text-xs"
            data-testid="breakdown-skipped-banner"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold mb-1">
                {skippedProducts.length} product{skippedProducts.length === 1 ? '' : 's'} excluded from Estimated Receipts — no selling price found.
              </p>
              <p className="mb-1">
                These products have stock movement (consumption &gt; 0) but no recorded sale in this session and no site price book entry.
                Add them to the site's price book or record a sale in this session to include them.
              </p>
              <ul className="list-disc pl-5 space-y-0.5 max-h-28 overflow-y-auto">
                {skippedProducts.slice(0, 12).map((p) => (
                  <li key={p.beverage_product_id || p.product_name} data-testid={`breakdown-skipped-${p.beverage_product_id}`}>
                    <span className="font-medium">{p.product_name}</span>
                    {p.category && <span className="text-rose-700/70"> · {p.category}</span>}
                    {p.consumption_qty != null && (
                      <span className="text-rose-700/70"> · consumed {Number(p.consumption_qty).toFixed(2)}</span>
                    )}
                  </li>
                ))}
                {skippedProducts.length > 12 && (
                  <li className="italic text-rose-700/70">
                    …and {skippedProducts.length - 12} more
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}

        {(isSearchable || filterFields.length > 0 || (columns && rows)) && (
          <div className="flex flex-wrap items-end gap-2">
            {isSearchable && (
              <div className="relative flex-grow min-w-[200px]" data-testid="breakdown-search-wrap">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search by product, brand or category…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="breakdown-search-input"
                />
              </div>
            )}
            {filterFields.map(f => (
              <div key={f.key} className="flex flex-col">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">{f.label}</label>
                <select
                  value={filterValues[f.key] || '__all__'}
                  onChange={e => setFilterValues(prev => ({ ...prev, [f.key]: e.target.value }))}
                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                  data-testid={`breakdown-filter-${f.key}`}
                >
                  <option value="__all__">All {f.label.toLowerCase().endsWith('y') ? f.label.toLowerCase().slice(0, -1) + 'ies' : f.label.toLowerCase() + 's'}</option>
                  {(filterOptions[f.key] || []).map(opt => (
                    <option key={opt} value={opt}>
                      {String(opt).replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {columns && rows && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                className="h-9 ml-auto"
                data-testid="breakdown-export-excel"
              >
                <Download className="h-4 w-4 mr-1" />
                Export Excel
              </Button>
            )}
          </div>
        )}

        {columns && rows && (
          <div className="border rounded overflow-x-auto" data-testid="breakdown-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={`px-3 py-2 font-medium text-slate-700 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(filteredRows || []).length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-4 text-center text-slate-500" data-testid="breakdown-empty-state">
                      {activeView?.emptyMessage
                        || ((isSearchable && search) || Object.values(filterValues).some(v => v && v !== '__all__')
                          ? 'No products match the current filters.'
                          : 'No data for this breakdown.')}
                    </td>
                  </tr>
                ) : groupedRows ? (
                  groupedRows.map(([groupKey, groupRows]) => {
                    // Subtotal column is the last numeric column with a fmt that returns currency
                    const totalsCol = columns[columns.length - 1];
                    const subtotal = groupRows.reduce((acc, r) => acc + (Number(r[totalsCol.key]) || 0), 0);
                    return (
                      <React.Fragment key={`group-${groupKey}`}>
                        <tr className="bg-slate-100 border-y" data-testid={`breakdown-group-${groupKey}`}>
                          <td colSpan={columns.length - 1} className="px-3 py-1.5 font-semibold text-slate-700 uppercase tracking-wide text-[11px]">
                            {String(groupKey).replace(/_/g, ' ')} <span className="text-slate-400 font-normal normal-case">({groupRows.length})</span>
                          </td>
                          <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                            {totalsCol.fmt ? totalsCol.fmt(subtotal) : subtotal}
                          </td>
                        </tr>
                        {groupRows.map((r, i) => (
                          <tr
                            key={`${groupKey}-${r.product_name || 'row'}-${i}`}
                            className="border-b last:border-0"
                          >
                            {columns.map(col => {
                              const raw = r[col.key];
                              const cell = col.fmt ? col.fmt(raw) : (raw != null ? String(raw) : '—');
                              return (
                                <td
                                  key={col.key}
                                  className={`px-3 py-1.5 tabular-nums ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                >
                                  {cell}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })
                ) : (
                  filteredRows.map((r, i) => (
                    <tr
                      key={`${r.product_name || 'row'}-${i}`}
                      className={`border-b last:border-0 ${r._isFooter ? 'bg-slate-50 italic' : ''}`}
                    >
                      {columns.map(col => {
                        const raw = r[col.key];
                        const cell = col.fmt ? col.fmt(raw) : (raw != null ? String(raw) : '—');
                        return (
                          <td
                            key={col.key}
                            className={`px-3 py-1.5 tabular-nums ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                          >
                            {cell}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
              {(totals || (footerRows && footerRows.length > 0)) && (
                <tfoot className="bg-slate-50 border-t">
                  {footerRows && footerRows.map((fr, idx) => (
                    <tr key={`footer-${idx}`}>
                      <td
                        colSpan={columns.length - 1}
                        className={`px-3 py-1.5 text-right font-medium ${fr.className || 'text-slate-600'}`}
                      >
                        {fr.label}
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${fr.className || 'text-slate-600'}`}>
                        {fr.fmt ? fr.fmt(fr.value) : fr.value}
                      </td>
                    </tr>
                  ))}
                  {totals && (
                    <tr className="border-t">
                      <td
                        colSpan={columns.length - 1}
                        className="px-3 py-2 font-semibold text-right"
                      >
                        {totals.label}
                      </td>
                      <td className="px-3 py-2 font-bold text-right tabular-nums">
                        {totals.fmt ? totals.fmt(totals.value) : totals.value}
                      </td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          </div>
        )}

        {!columns && totals && (
          <div className="border-t pt-2 flex items-center justify-between">
            <span className="text-sm font-semibold">{totals.label}</span>
            <span className="text-base font-bold tabular-nums">
              {totals.fmt ? totals.fmt(totals.value) : totals.value}
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
